import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, ExternalLink, Info, Wrench, X, XOctagon } from "lucide-react";

import {
  deveMostrarStatus,
  lerStatusDaPagina,
  type EstadoDaPlataforma,
  type StatusDaPlataforma,
} from "../../lib/status-da-plataforma";

/**
 * Banner de status da plataforma (doc: docs/funcionalidades/banner-de-status.md).
 *
 * Lê o JSON público da status page da Better Stack a cada `intervaloMs` (60 s) com a aba
 * visível, ao voltar à aba/foco e ao reconectar. Aparece só fora do operacional; "Fechar"
 * dispensa ESTA situação na sessão — se o quadro mudar, volta.
 *
 * Leitura que falha não acende nada e mantém o último quadro bom por até `validadeMs`
 * (5 min); depois disso o quadro velho cai — banner vermelho preso porque a status page
 * ficou inacessível é pior que nenhum. A queda da nossa API tem sinal próprio (faixa
 * "Conexão instável"), que se posiciona logo abaixo deste banner pela variável CSS
 * `--pps-aviso-de-status-altura`.
 *
 * `url` nulo = desligado (fora de PROD sem override). Textos vêm do app (o pacote não tem i18n).
 */
export interface AvisoDeStatusTextos {
  fora: string;
  degradado: string;
  manutencao: string;
  informativo: string;
  /** "Afetados: CRM, API" */
  afetados: (lista: string) => string;
  verStatus: string;
  fechar: string;
}

export interface AvisoDeStatusProps {
  url: string | null | undefined;
  textos: AvisoDeStatusTextos;
  intervaloMs?: number;
  validadeMs?: number;
  /** Injetável para teste; padrão: fetch do `index.json` sem cache. */
  buscar?: (url: string) => Promise<unknown>;
}

/** Altura (px) do banner visível, para a faixa "Conexão instável" ficar logo abaixo. */
export const VARIAVEL_ALTURA_DO_AVISO = "--pps-aviso-de-status-altura";

const CHAVE_DISPENSA = "pps:aviso-de-status-dispensado";
const TIMEOUT_MS = 10_000;
const FOCO_MINIMO_MS = 15_000;

async function buscarPadrao(url: string): Promise<unknown> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(`${url}/index.json`, { cache: "no-store", signal: ctrl.signal });
    if (!r.ok) throw new Error(`status ${r.status}`);
    return await r.json();
  } finally {
    clearTimeout(t);
  }
}

function lerDispensa(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DISPENSA);
  } catch {
    return null;
  }
}

function gravarDispensa(assinatura: string) {
  try {
    sessionStorage.setItem(CHAVE_DISPENSA, assinatura);
  } catch {
    /* sem storage: dispensa só nesta tela */
  }
}

function abaVisivel(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

const VISUAL: Record<Exclude<EstadoDaPlataforma, "operacional">, { classe: string; Icone: typeof AlertTriangle }> = {
  fora: {
    classe: "border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100",
    Icone: XOctagon,
  },
  degradado: {
    classe: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100",
    Icone: AlertTriangle,
  },
  manutencao: {
    classe: "border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-700 dark:bg-sky-950 dark:text-sky-100",
    Icone: Wrench,
  },
  informativo: {
    classe: "border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-700 dark:bg-sky-950 dark:text-sky-100",
    Icone: Info,
  },
};

export function AvisoDeStatus({
  url,
  textos,
  intervaloMs = 60_000,
  validadeMs = 5 * 60_000,
  buscar = buscarPadrao,
}: AvisoDeStatusProps) {
  const [status, setStatus] = useState<StatusDaPlataforma | null>(null);
  const [dispensada, setDispensada] = useState<string | null>(() => lerDispensa());
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;
  const ultimaTentativa = useRef(0);
  const ultimoOk = useRef(0);
  const emCurso = useRef(false);
  const caixa = useRef<HTMLDivElement | null>(null);

  const checar = useCallback(async () => {
    if (!url || emCurso.current) return;
    emCurso.current = true;
    ultimaTentativa.current = Date.now();
    try {
      const lido = lerStatusDaPagina(await buscarRef.current(url));
      if (!lido) throw new Error("formato desconhecido");
      ultimoOk.current = Date.now();
      setStatus(lido);
    } catch {
      // Falha não é incidente: mantém o último quadro bom, mas só enquanto ele vale.
      if (Date.now() - ultimoOk.current > validadeMs) setStatus(null);
    } finally {
      emCurso.current = false;
    }
  }, [url, validadeMs]);

  useEffect(() => {
    if (!url) {
      setStatus(null);
      return;
    }
    void checar();
    // Aba escondida não consulta (recepção deixa o CRM aberto o dia todo em segundo plano):
    // a volta à aba dispara a leitura pelo `visibilitychange`.
    const timer = setInterval(() => {
      if (abaVisivel()) void checar();
    }, intervaloMs);
    const aoVoltar = () => {
      if (!abaVisivel()) return;
      if (Date.now() - ultimaTentativa.current >= FOCO_MINIMO_MS) void checar();
    };
    window.addEventListener("focus", aoVoltar);
    window.addEventListener("online", aoVoltar);
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", aoVoltar);
      window.removeEventListener("online", aoVoltar);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [url, intervaloMs, checar]);

  const visivel = !!url && !!status && deveMostrarStatus(status, dispensada);

  // Publica a altura do banner para a faixa "Conexão instável" ficar logo abaixo (no celular
  // o detalhe quebra linha e a altura varia). Sem banner, a variável sai e a faixa volta ao topo.
  useLayoutEffect(() => {
    const raiz = typeof document !== "undefined" ? document.documentElement : null;
    if (!raiz) return;
    const el = caixa.current;
    if (!visivel || !el) {
      raiz.style.removeProperty(VARIAVEL_ALTURA_DO_AVISO);
      return;
    }
    const publicar = () => raiz.style.setProperty(VARIAVEL_ALTURA_DO_AVISO, `${Math.ceil(el.getBoundingClientRect().height)}px`);
    publicar();
    const obs = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publicar) : null;
    obs?.observe(el);
    return () => {
      obs?.disconnect();
      raiz.style.removeProperty(VARIAVEL_ALTURA_DO_AVISO);
    };
  }, [visivel, status?.assinatura]);

  if (!visivel) return null;
  const estado = status!.estado as Exclude<EstadoDaPlataforma, "operacional">;
  const { classe, Icone } = VISUAL[estado];
  const titulo = textos[estado];
  const detalhe =
    status!.aviso?.titulo ?? (status!.afetados.length ? textos.afetados(status!.afetados.join(", ")) : null);

  return (
    // O contêiner deixa os cliques passarem: só a pílula bloqueia o que estiver por baixo.
    <div
      role="status"
      aria-live="polite"
      data-pps-component="aviso-de-status"
      data-estado={estado}
      className="pointer-events-none fixed inset-x-0 top-2 z-[100] flex justify-center px-4"
    >
      <div
        ref={caixa}
        className={`pointer-events-auto flex w-full max-w-2xl items-start gap-2 rounded-lg border px-3 py-1.5 text-sm shadow-sm sm:items-center ${classe}`}
      >
        <Icone className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" aria-hidden="true" />
        <p className="min-w-0 flex-1">
          <span className="font-semibold">{titulo}</span>
          {detalhe && (
            <span className="line-clamp-2 block break-words sm:line-clamp-1">{detalhe}</span>
          )}
        </p>
        <a
          href={url!}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 font-medium underline underline-offset-2"
        >
          {textos.verStatus}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        <button
          type="button"
          onClick={() => {
            gravarDispensa(status!.assinatura);
            setDispensada(status!.assinatura);
          }}
          aria-label={textos.fechar}
          title={textos.fechar}
          className="-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-black/5 dark:hover:bg-white/10"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
